import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

const apiUrl = 'api/public/transfer';

const initialState = {
  loading: false,
  loadError: false,
  status: null as 'ACTIVE' | 'COMPLETED' | 'INVALID' | null,
  ecertNo: null as string | null,
  maskedPhone: null as string | null,

  sendingOtp: false,
  otpSent: false,
  otpError: null as string | null,

  verifying: false,
  verifyError: null as string | null,
  verifySuccess: false,
  idToken: null as string | null,
};

export type TransferOtpState = Readonly<typeof initialState>;

// The backend (TransferOtpService) returns a plain-English reason in the problem body's
// `detail`/`message` field. Map the known reasons to i18n keys so the error still renders
// correctly in Arabic - anything unrecognized falls back to a generic translated message.
const ERROR_KEY_MAP: Array<[string, string]> = [
  ['already been used', 'transferOtp.errors.alreadyUsed'],
  ['expired', 'transferOtp.errors.expired'],
  ['Too many incorrect', 'transferOtp.errors.tooManyAttempts'],
  ['Incorrect code', 'transferOtp.errors.incorrect'],
  ['wait before requesting', 'transferOtp.errors.throttled'],
  ['No phone number', 'transferOtp.errors.noPhone'],
];

const extractErrorMessage = (error: any): string => {
  const detail: string = error?.response?.data?.detail || error?.response?.data?.message || '';
  const match = ERROR_KEY_MAP.find(([needle]) => detail.includes(needle));
  return match ? match[1] : 'transferOtp.errors.generic';
};

// The global axios interceptor resolves failed requests with the error instead of rejecting,
// so a failed call has to be detected here rather than relying on try/catch alone.
const throwIfFailed = (result: any) => {
  if (!result || result.isAxiosError || result instanceof Error) {
    throw result;
  }
  return result;
};

export const getTransferStatus = createAsyncThunk('transferOtp/get_status', async (quotationId: string) => {
  const result = throwIfFailed(await axios.get(`${apiUrl}/${quotationId}`));
  return result.data;
});

export const requestTransferOtp = createAsyncThunk(
  'transferOtp/request_otp',
  async (quotationId: string, { rejectWithValue }) => {
    try {
      throwIfFailed(await axios.post(`${apiUrl}/${quotationId}/otp`));
      return true;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const verifyTransferOtp = createAsyncThunk(
  'transferOtp/verify_otp',
  async ({ quotationId, code }: { quotationId: string; code: string }, { rejectWithValue }) => {
    try {
      const result = throwIfFailed(await axios.post(`${apiUrl}/${quotationId}/otp/verify`, { code }));
      return result.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const TransferOtpSlice = createSlice({
  name: 'transferOtp',
  initialState: initialState as TransferOtpState,
  reducers: {
    resetTransferOtp() {
      return initialState;
    },
    resetOtpError(state) {
      state.otpError = null;
    },
    resetVerifyError(state) {
      state.verifyError = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getTransferStatus.pending, state => {
        state.loading = true;
        state.loadError = false;
      })
      .addCase(getTransferStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.status = action.payload.status;
        state.ecertNo = action.payload.ecertNo;
        state.maskedPhone = action.payload.maskedPhone;
      })
      .addCase(getTransferStatus.rejected, state => {
        state.loading = false;
        state.loadError = true;
        state.status = 'INVALID';
      })
      .addCase(requestTransferOtp.pending, state => {
        state.sendingOtp = true;
        state.otpError = null;
      })
      .addCase(requestTransferOtp.fulfilled, state => {
        state.sendingOtp = false;
        state.otpSent = true;
      })
      .addCase(requestTransferOtp.rejected, (state, action) => {
        state.sendingOtp = false;
        state.otpError = (action.payload as string) || 'transferOtp.errors.generic';
      })
      .addCase(verifyTransferOtp.pending, state => {
        state.verifying = true;
        state.verifyError = null;
      })
      .addCase(verifyTransferOtp.fulfilled, (state, action) => {
        state.verifying = false;
        state.verifySuccess = true;
        state.idToken = action.payload.idToken;
      })
      .addCase(verifyTransferOtp.rejected, (state, action) => {
        state.verifying = false;
        state.verifyError = (action.payload as string) || 'transferOtp.errors.generic';
      });
  },
});

export const { resetTransferOtp, resetOtpError, resetVerifyError } = TransferOtpSlice.actions;

export default TransferOtpSlice.reducer;
